import * as CL from '@design-system/component-library';
import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { paths } from '../../../../../common/constants/pathVariables.js';
import { DeviceVariantProductTypeCoCo } from '../../../../../common/enums.js';
import { fetchEmployeeProductSelection } from '../../../../../common/fetch.js';
import { employeeOmaElisaMsg, shopMsg, systemErrorOccurredMsg, t } from '../../../../../common/i18n/index.js';
import { loadDeviceVariantGroupsCoCo } from '../../../../../common/loaders.js';
import { getDeviceSelectionProductTypeLabel } from '../../../../../common/utils/deviceListTabsUtilsCoCo.js';
import { toDeviceSelectionProductType } from '../../../../../common/utils/deviceSelectionListUtilsCoCo.js';
import { EoeDeviceStoreProductsCoCo } from '../../../../../components/EoeDeviceStoreProductsCoCo/EoeDeviceStoreProductsCoCo.js';
import { Grid } from '../../../../../components/Grid/Grid.js';
import { HeroHeading, HeroHeadingType } from '../../../../../components/HeroHeading/index.js';
import { useAuth } from '../../../AuthProvider.js';
import type { DeviceVariantGroupsByTypeCoCo } from '../../../../../common/utils/deviceSelectionDeviceUtilsCoCo.js';
import type { EmployeeProductListResponse } from '../../../../../generated/api/employeeProductListResponse.js';

const EMPTY_VARIANT_GROUPS: DeviceVariantGroupsByTypeCoCo = {
  [DeviceVariantProductTypeCoCo.Device]: [],
  [DeviceVariantProductTypeCoCo.EPP]: [],
};

const EmployeeDeviceListProducts = ({ list }: { list: EmployeeProductListResponse }) => {
  const navigate = useNavigate();
  const { authenticatedUser } = useAuth();
  const mdmId = authenticatedUser?.mdmId;
  const selectionUuid = list.employeeProductSelectionUuid;
  const [data, setData] = useState<{
    variantGroups: DeviceVariantGroupsByTypeCoCo;
    allowedVariantGroupIds: string[];
  }>();
  const [hasLoadError, setHasLoadError] = useState(false);

  useEffect(() => {
    if (!selectionUuid || !mdmId) {
      return;
    }
    let isCurrentRequest = true;
    setData(undefined);
    setHasLoadError(false);
    const loadProducts = async () => {
      try {
        const selection = await fetchEmployeeProductSelection(selectionUuid, mdmId);
        const variantGroups = selection.items.length > 0 ? await loadDeviceVariantGroupsCoCo() : EMPTY_VARIANT_GROUPS;
        if (isCurrentRequest) {
          setData({ variantGroups, allowedVariantGroupIds: selection.items.map(item => item.variantGroupId) });
        }
      } catch {
        if (isCurrentRequest) {
          setHasLoadError(true);
        }
      }
    };
    void loadProducts();
    return () => {
      isCurrentRequest = false;
    };
  }, [mdmId, selectionUuid]);

  if (hasLoadError) {
    return <p role="alert">{t.JCJF(systemErrorOccurredMsg)}</p>;
  }
  if (selectionUuid && mdmId && !data) {
    return <CL.LoadingSpinner size="xl" />;
  }
  return (
    <EoeDeviceStoreProductsCoCo
      productType={toDeviceSelectionProductType(list.productType)}
      variantGroups={data?.variantGroups ?? EMPTY_VARIANT_GROUPS}
      allowedVariantGroupIds={data?.allowedVariantGroupIds ?? []}
      onSelectDevice={path => navigate(path, { state: { employeeProductList: list } })}
    />
  );
};

export const EoeStoreProductsCoCo = () => {
  const { state } = useLocation();
  const employeeProductList = state?.employeeProductList as EmployeeProductListResponse | undefined;

  if (!employeeProductList) {
    return <Navigate to={paths.EMPLOYEE_DEVICE_STORE} replace />;
  }

  return (
    <>
      <HeroHeading
        center={true}
        fullScreen={true}
        breadCrumbPaths={[
          { name: t.H6JV(employeeOmaElisaMsg), path: paths.EMPLOYEE_HOME },
          { name: t.BZGP(shopMsg), path: paths.EMPLOYEE_DEVICE_STORE },
          { name: employeeProductList.name ?? '' },
        ]}
        heroHeadingType={HeroHeadingType.BLANK}
        title={employeeProductList.name ?? ''}
        subtitle={getDeviceSelectionProductTypeLabel(toDeviceSelectionProductType(employeeProductList.productType))}
      />
      <Grid colWidthL={10}>
        <EmployeeDeviceListProducts
          key={`${employeeProductList.uuid}-${employeeProductList.employeeProductSelectionUuid}`}
          list={employeeProductList}
        />
      </Grid>
    </>
  );
};
